import { Keypair } from 'tokend-js-sdk'

import { DocumentContainer } from '@js/helpers/DocumentContainer'
import { StateHelper } from '../helpers/state.helper'
import { usersService } from '@api/users.service'
import { vuexTypes } from '../types'
import { RecordFactory } from '@js/records/factory'

import { accountsService } from '@api/accounts.service'
import { initService } from '@api/init.service'
import { reviewableRequestsService } from '@api/reviewable_requests.service'
import { fileService } from '@api/file.service'
import { ACCOUNT_STATES, filterHiddenAssets } from '@const'
import get from 'lodash/get'

export const state = {
  account: {
    external_system_accounts: []
  },
  balances: [],
  investmentBalances: [],
  limits: [],
  keys: {
    accountId: '',
    publicKey: '',
    seed: ''
  },
  // kyc:
  kycRequests: [],
  kycData: {
    address: {},
    documents: {}
  },
  kycDocuments: {},
  signer: {
    signer_name: '',
    weight: 0,
    signer_type: 0,
    identity_id: 0
  },
  masterAccountId: '',
  factorData: {},
  approverData: {}
}

export const mutations = {
  SET_APPROVER_DATA (state, data) {
    state.approverData = data
  },
  SET_ACCOUNT_KEYS (state, keys) {
    state.keys = keys
  },

  SET_ACCOUNT_DETAILS (state, account) {
    state.account = account
  },

  SET_ACCOUNT_SIGNER (state, signer) {
    if (signer) state.signer = signer
  },

  SET_ACCOUNT_BALANCES (state, balances) {
    state.balances = balances
  },

  SET_ACCOUNT_INVESTMENT_BALANCES (state, balances) {
    state.investmentBalances = balances
  },

  SET_ACCOUNT_LIMITS (state, limits) {
    state.limits = limits
  },

  SET_ACCOUNT_KYC_REQUESTS (state, requests) {
    state.kycRequests = requests
  },

  SET_ACCOUNT_KYC_DATA (state, data) {
    state.kycData = data
  },

  SET_ACCOUNT_KYC_TYPE (state, data) {
    state.kycAccountType = data
  },

  SET_ACCOUNT_KYC_DOCUMENTS (state, documents) {
    state.kycDocuments = Object.entries(documents)
      .reduce((docs, [type, doc]) => {
        const opts = doc.front || doc
        if (opts && (doc.key || doc.file || doc.front)) {
          opts['type'] = type
          return {...docs, [type]: new DocumentContainer(opts)}
        } else {
          return {...docs, [type]: null}
        }
      }, {})
  },
  SET_SERVER_CONFIG (state, master) {
    state.historyLatestLedger = master.history_latest_ledger
    state.historyElderLedger = master.history_elder_ledger
    state.coreLatestLedger = master.core_latest_ledger
    state.coreElderLedger = master.core_elder_ledger
    state.networkPassphrase = master.network_passphrase
    state.commissionAccountId = master.commission_account_id
    state.operationalAccountId = master.operational_account_id
    state.storageFeeAccountId = master.storage_fee_account_id
    state.exchangeOperatorAccountId = master.exchange_operator_account_id
    state.masterAccountId = master.master_account_id
    state.masterExchangeName = master.master_exchange_name
    state.txExpirationPeriod = master.tx_expiration_period
    state.currentTime = master.current_time
    state.precision = master.precision
    state.cryptoPrecision = master.crypto_precision
    state.fiatPrecision = master.fiat_precision
    state.cryptoCurrencies = master.crypto_currencies
  },
  SET_MASTER_ACCOUNT_ID (state, master) {
    state.masterAccountId = master
  },

  SET_OPERATIONAL_ACCOUNT_ID (state, operational) {
    state.operationalAccountId = operational
  },

  SET_FACTOR_DATA (state, data) {
    state.factorData = data
  }
}

export const actions = {
  setApproverData ({ commit }, data) {
    commit('SET_APPROVER_DATA', data)
  },
  async GET_ACCOUNT_DETAILS ({ commit }) {
    commit(vuexTypes.SET_ACCOUNT_DETAILS, await accountsService.loadAccount())
  },

  async GET_ACCOUNT_BALANCES ({ commit }) {
    let balances = await accountsService.loadDetailsForEachBalance()
    commit(vuexTypes.SET_ACCOUNT_BALANCES, filterHiddenAssets(balances))
  },

  async GET_ACCOUNT_SIGNER ({ commit }) {
    const signer = await accountsService.loadSigners()
    if (signer) commit(vuexTypes.SET_ACCOUNT_SIGNER, signer)
  },

  async GET_ACCOUNT_INVESTMENT_BALANCES ({ commit }) {
    const balances = await accountsService.loadDetailsForEachBalance()
    const investmentBalances = balances.filter(asset => get(asset, 'asset_details.sales'))
    commit(vuexTypes.SET_ACCOUNT_INVESTMENT_BALANCES, investmentBalances.map(record => RecordFactory.createInvestmentTokenRecord(record)))
  },

  async GET_ACCOUNT_KYC_REQUESTS ({ commit }) {
    const requests = await reviewableRequestsService.loadKycReviewableRequests()
    commit(vuexTypes.SET_ACCOUNT_KYC_REQUESTS, requests.records.map(record => RecordFactory.createKycRequestRecord(record)))
  },

  async GET_ACCOUNT_LIMITS ({ commit }) {
    const limits = await accountsService.loadAccountLimits()
    commit(vuexTypes.SET_ACCOUNT_LIMITS, (limits.limits || []).map(limit => RecordFactory.createLimitRecord(limit)))
  },

  async GET_ACCOUNT_KYC_DATA ({ commit }, opts) {
    const kycData = await usersService
      .blobsOf()
      .get(opts.blobId)
    commit(vuexTypes.SET_ACCOUNT_KYC_TYPE, opts.type)
    commit(vuexTypes.SET_ACCOUNT_KYC_DATA, kycData)
    if (kycData.documents) {
      commit(vuexTypes.SET_ACCOUNT_KYC_DOCUMENTS, kycData.documents)
    }
  },

  async GET_SERVER_CONFIG ({ commit }) {
    const config = await initService.getServerConfig()
    commit(vuexTypes.SET_SERVER_CONFIG, config._parsedData)
  },

  async GET_MASTER_ACCOUNT_ID ({ commit }) {
    const config = await initService.getServerConfig()
    commit(vuexTypes.SET_MASTER_ACCOUNT_ID, config._parsedData.master_account_id)
  },

  async GET_OPERATIONAL_ACCOUNT_ID ({ commit }) {
    const config = await initService.getServerConfig()
    commit(vuexTypes.SET_OPERATIONAL_ACCOUNT_ID, config._parsedData.exchange_operator_account_id)
  },

  async UPDATE_ACCOUNT_KYC_DOCUMENTS ({ commit }, documents) {
    async function uploadBothDocSides ({ front, back }) {
      if (front && !front.isUploaded) {
        front.setKey(await fileService.uploadFile(front.getDetailsForUpload()))
      }
      if (back && !back.isUploaded) {
        back.setKey(await fileService.uploadFile(back.getDetailsForUpload()))
      }
    }

    await Promise.all(Object.values(documents).map(document =>
      document ? uploadBothDocSides(document) : function () {}))
  },

  /**
   * @param commit
   * @param {object} opts
   * @param {object} opts.details - KYC details from form
   * @param {documents} opts.documents - Containers instances with uploaded documents
   * (separate for document.front/document.back) {@link DocumentContainer.getDetailsForSave}
   * @returns {Promise<string>} - blobId
   * @constructor
   */
  async UPDATE_ACCOUNT_KYC_DATA ({ commit }, opts) {
    const data = {
      ...opts.details,
      documents: opts.documents
    }
    return (await usersService.blobsOf()
      .create(opts.blobType, data))
      .data('id')
  },

  SET_FACTOR_DATA ({ commit }, data) {
    commit(vuexTypes.SET_FACTOR_DATA, data)
  }
}

export const getters = {
  approverData: state => state.approverData,
  historyLatestLedger: state => state.historyLatestLedger,
  historyElderLedger: state => state.historyElderLedger,
  coreLatestLedger: state => state.coreLatestLedger,
  networkPassphrase: state => state.networkPassphrase,
  commissionAccountId: state => state.commissionAccountId,
  operationalAccountId: state => state.operationalAccountId,
  storageFeeAccountId: state => state.storageFeeAccountId,
  exchangeOperatorAccountId: state => state.exchangeOperatorAccountId,
  masterAccountId: state => state.masterAccountId,
  masterExchangeName: state => state.masterExchangeName,
  txExpirationPeriod: state => state.txExpirationPeriod,
  currentTime: state => state.currentTime,
  precision: state => state.precision,
  cryptoPrecision: state => state.cryptoPrecision,
  fiatPrecision: state => state.fiatPrecision,
  cryptoCurrencies: state => state.cryptoCurrencies,
  account: state => state.account,
  accountId: state => state.keys.accountId,
  accountType: state => state.account.account_type,
  accountTypeI: state => state.account.account_type_i,
  accountBlocked: state => state.account.is_blocked,
  accountBlockedReasons: state => state.account.block_reasons,
  accountSignerName: state => state.signer.signer_name,
  accountSignerType: state => state.signer.signer_type,
  accountSignerIdentity: state => state.signer.identity_id,
  accountSignerWeight: state => state.signer.weight,
  accountSeed: state => state.keys.seed,
  accountPublicKey: state => state.keys.publicKey,
  accountKeypair: state => state.keys.seed ? Keypair.fromSecret(state.keys.seed) : null,
  accountCreatedAt: state => state.account.createdAt,
  accountTokens: state => state.balances.map(balance => balance.asset),
  accountBalances: state => StateHelper.groupBalances(state.balances),
  accountInvestmentBalances: state => state.investmentBalances,
  accountRawBalances: state => state.balances,
  accountOwnedTokens: state =>
    state.balances
    .filter((balance) => { return balance.account_id === balance.asset_details.owner })
    .map(balance => balance.asset_details.code),
  accountDepositAddresses: state =>
    state.account.external_system_accounts
      .map(account => RecordFactory.createExternalAccountRecord(account))
      .reduce((accounts, account) => { accounts[account.type] = account.address; return accounts }, {}),
  accountLimits: state => state.limits,
  // kyc:
  accountKycRequests: state => state.kycRequests,
  accountKycLatestRequest: state => StateHelper.defineLatestKycRequest(state),
  accountState: (state, getters) => ACCOUNT_STATES[getters.accountKycLatestRequest.state] || ACCOUNT_STATES.nil,
  accountLatestBlobId: (state, getters) => getters.accountKycLatestRequest.blobId,
  accountKycData: state => state.kycData,
  accountKycDocuments: state => state.kycDocuments,
  accountLatestKycLevel: (state, getters) => getters.accountKycLatestRequest.kycLevel,
  factorData: state => state.factorData

}

export default {
  actions,
  getters,
  mutations,
  state
}



// WEBPACK FOOTER //
// ./src/vuex/modules/account.module.js